import { Injectable } from '@angular/core';
import { feedsConfig } from './constants';

export interface AvailableFeedFilterType {
  type: string;
  label: string;
  feedKeys: string[];
  colorMap?: any;
  isVesselLength?: boolean;
  isOilSpillPrediction?: boolean;
  tooltipLabel?: string;
  popupLabel?: string;
  placeholderLabel?: string;
}

export type FeedFilterState = {
  feedKeys: string[];
  value: any;
  isGreater?: boolean;
};

export type ActiveFeedFilters = {
  classByFeed: Map<string, { type: string; value: any }>;
  vesselLengthByFeed: Map<string, { value: number; isGreater: boolean }>;
};

export type FeedFilterSelectionState = {
  selectedFilterType: string | null;
  filtersByType: Map<string, FeedFilterState>;
};

type ClassBasedFilterCopy = {
  label: string;
  tooltip: string;
  placeholder: string;
};

const CLASS_BASED_FILTER_COPY: Record<string, ClassBasedFilterCopy> = {
  'Soil Moisture - SAR': { label: 'Filter by soil moisture class', tooltip: 'Filter by soil moisture class', placeholder: 'Select soil moisture level' },
  'Vessel-classification': { label: 'Filter by vessel classification', tooltip: 'Filter by vessel classification', placeholder: 'Select vessel type' },
  'Vehicle-classification': { label: 'Filter by vehicle type', tooltip: 'Filter by vehicle classification', placeholder: 'Select vehicle type' },
  'Flood Inundation': { label: 'Filter by flood inundation depth', tooltip: 'Filter by flood inundation depth', placeholder: 'Select flood depth' },
  'Land use/land cover detection': { label: 'Filter by land use/land cover', tooltip: 'Filter by land use/land cover', placeholder: 'Select category' },
};

@Injectable({ providedIn: 'root' })
export class FeedFilterService {
  buildSingleFeedScope(infoService: any): any {
    return {
      ...infoService,
      filterScopeKey: `feed:${infoService?._id}`,
      InfoServices: infoService ? [infoService] : [],
    };
  }

  shouldShowFilterOption(sensor: any): boolean {
    return this.getAvailableFilterTypesForSensor(sensor).length > 0;
  }

  isFilterOptionDisabled(sensor: any, mapLoading: boolean): boolean {
    return mapLoading || this.getAvailableFilterTypesForSensor(sensor, true).length === 0;
  }

  getFilterTooltip(
    sensor: any,
    state: FeedFilterSelectionState,
    mapLoading: boolean
  ): string {
    if (!this.shouldShowFilterOption(sensor)) return '';

    const available = this.getAvailableFilterTypesForSensor(sensor, true);
    if (available.length === 0) return 'Check the feed to enable filter';
    if (mapLoading) return 'Filter will be enabled after the feed finishes loading';

    if (state.selectedFilterType === 'vesselLength' && state.filtersByType.has('vesselLength')) {
      return 'Filter by vessel length';
    }

    const selected = available.find((item) => item.type === state.selectedFilterType);
    if (selected?.tooltipLabel || selected?.label) {
      return selected.tooltipLabel || selected.label;
    }

    const classBased = available.find((item) => item.type !== 'vesselLength');
    return classBased?.tooltipLabel || classBased?.label || 'Filter by vessel length';
  }

  buildActiveFilters(filters: Map<string, FeedFilterState>): ActiveFeedFilters {
    const classByFeed = new Map<string, { type: string; value: any }>();
    const vesselLengthByFeed = new Map<string, { value: number; isGreater: boolean }>();

    for (const [type, filter] of filters.entries()) {
      if (!filter || filter.value == null) continue;

      if (type === 'vesselLength') {
        const value = Number(filter.value);
        if (!Number.isNaN(value)) {
          filter.feedKeys.forEach((key) =>
            vesselLengthByFeed.set(String(key), { value, isGreater: !!filter.isGreater })
          );
        }
        continue;
      }

      if (type === 'oilSpillPrediction') {
        continue;
      }

      filter.feedKeys.forEach((key) =>
        classByFeed.set(String(key), { type, value: filter.value })
      );
    }

    return { classByFeed, vesselLengthByFeed };
  }

  filterFeatures(
    features: any[],
    feedType: string | undefined,
    classFilter?: { type: string; value: any },
    vesselLengthFilter?: { value: number; isGreater: boolean }
  ): any[] {
    const selectedClass = classFilter
      ? this.normalizeClassKey(classFilter.value, feedType)
      : null;

    return features.filter((feature) => {
      if (selectedClass !== null && this.resolveClassKey(feature, feedType) !== selectedClass) {
        return false;
      }

      return !vesselLengthFilter || this.matchesVesselLength(
        feature,
        vesselLengthFilter.value,
        vesselLengthFilter.isGreater
      );
    });
  }

  resolveClassKey(feature: any, feedType?: string): string {
    const value = feedType === 'Flood Inundation'
      ? feature?.properties?.depth_class ?? feature?.properties?.depth_clas ?? feature?.properties?.class
      : feature?.properties?.class;
    return this.normalizeClassKey(value, feedType);
  }

  matchesVesselLength(feature: any, length: number, keepGreater: boolean): boolean {
    const value = parseFloat(
      String(feature?.properties?.vessel_length ?? feature?.properties?.vessel_len ?? '')
        .replace(/[^\d.]/g, '')
    ) || 0;
    return !Number.isNaN(value) && (keepGreater ? value > length : value < length);
  }

  getResultKey(feedType?: string): 'vessel_count' | 'transhipment_count' {
    return String(feedType ?? '').trim().toLowerCase().includes('trans')
      ? 'transhipment_count'
      : 'vessel_count';
  }

  countByClass(features: any[], feedType?: string): Record<string, number> {
    return features.reduce((counts, feature) => {
      const key = this.resolveClassKey(feature, feedType) || 'Unknown';
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);
  }

  getClassCountsForLegend(feedType: string | undefined, features: any[]): Record<string, number> | undefined {
    return feedType === 'Vessel-classification' || feedType === 'Vehicle-classification'
      ? this.countByClass(features, feedType)
      : undefined;
  }

  private normalizeClassKey(value: any, feedType?: string): string {
    if (value == null) return '';
    const key = String(value).trim();
    return feedType === 'Flood Inundation'
      ? key.replace(/[\u2010-\u2015\u2212\-\?]/g, '–').replace(/\s+/g, ' ').trim()
      : key;
  }

  isSupportedFeed(infoService: any): boolean {
    const config = feedsConfig[infoService?._id];
    if (!config) return false;

    return ['ship', 'vessel'].includes(String(config.type ?? '').toLowerCase()) ||
      config?.type === 'Oil-spill-prediction' ||
      this.getClassBasedFilterDefinition(config) !== null;
  }

  getSupportedFeedKeys(sensor: any, onlyChecked = false): string[] {
    if (!Array.isArray(sensor?.InfoServices)) return [];

    return sensor.InfoServices
      .filter((feed: any) => this.isSupportedFeed(feed) && (!onlyChecked || feed?.checked === true))
      .map((feed: any) => String(feed?._id ?? feed?.name ?? feed?.feedName ?? ''))
      .filter(Boolean);
  }

  getAvailableFilterTypesForSensor(sensor: any, onlyChecked = false): AvailableFeedFilterType[] {
    return this.getAvailableFilterTypesForFeedKeys(this.getSupportedFeedKeys(sensor, onlyChecked));
  }

  getAvailableFilterTypesForFeedKeys(feedKeys: string[]): AvailableFeedFilterType[] {
    const types = new Map<string, AvailableFeedFilterType>();

    for (const feedKey of feedKeys) {
      const config = feedsConfig[feedKey];
      if (!config) continue;

      if (['ship', 'vessel'].includes(String(config.type ?? '').toLowerCase())) {
        this.addFilterType(types, { type: 'vesselLength', label: 'Filter by vessel length', feedKeys: [feedKey], isVesselLength: true });
        continue;
      }

      if (config?.type === 'Oil-spill-prediction') {
        this.addFilterType(types, {
          type: 'oilSpillPrediction',
          label: 'Filter by oil spill prediction',
          feedKeys: [feedKey],
          isOilSpillPrediction: true,
          tooltipLabel: 'Filter by detection and forecast hour',
          popupLabel: 'Filter by oil spill prediction',
          placeholderLabel: 'Select detection',
        });
        continue;
      }

      const definition = this.getClassBasedFilterDefinition(config);
      if (definition) this.addFilterType(types, { ...definition, feedKeys: [feedKey] });
    }

    return Array.from(types.values());
  }

  private addFilterType(types: Map<string, AvailableFeedFilterType>, candidate: AvailableFeedFilterType): void {
    const existing = types.get(candidate.type);
    if (existing) {
      existing.feedKeys.push(...candidate.feedKeys);
      return;
    }
    types.set(candidate.type, candidate);
  }

  private getClassBasedFilterDefinition(config: any): Omit<AvailableFeedFilterType, 'feedKeys'> | null {
    const colorMap = config?.color;
    if (config?.colorMode !== 'CLASS_BASED' || !colorMap || typeof colorMap !== 'object' || Object.keys(colorMap).length <= 2) {
      return null;
    }

    const feedType = String(config?.type ?? '').trim();
    if (!feedType) return null;

    const copy = CLASS_BASED_FILTER_COPY[feedType];
    const label = config?.filterLabel || copy?.label || `Filter by ${feedType.toLowerCase()}`;
    return {
      type: config?.filterType || `class:${feedType.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      label,
      colorMap,
      tooltipLabel: config?.tooltipLabel || copy?.tooltip || label,
      popupLabel: label,
      placeholderLabel: config?.placeholderLabel || copy?.placeholder || `Select ${feedType.toLowerCase()}`,
    };
  }
}
